import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../store/rootReducer';
import { IExtension } from '../../models/Extension';
import CreateWorkspaceDialog from '../Workspace/CreateWorkspaceDialog';
import ContactDetailsModal from '../Contacts/ContactDetailsModal';
import CompanyDetailsModal from '../Contacts/CompanyDetailsModal';
import NewApiKeyDialog from '../ApiKeyManagement/NewApiKeyDialog';
import WebhookPanel from '../Webhooks/WebhookPanel';
import EditTaxCodeModal from '../TaxCodeModals/EditTaxCodeModal';
import AvailableExtensionsPanel from '../ExtensionInstallation/AvailableExtensionsPanel';
import ExtensionDetailsPanel from '../ExtensionInstallation/ExtensionDetailsPanel';
import ExtensionFolioDetailsPanel from '../ExtensionFolios/ExtensionFolioDetailsPanel';
import ProductIdModal from '../DetailsModal/ProductIdModal';
import StorefrontOrderPanel from '../Storefront/Orders/StorefrontOrderPanel';
import ProductCategoryDetailsPanel from '../ProductCategorization/ProductCategoryDetailsPanel';
import ProductSegmentDetailsPanel from '../ProductCategorization/ProductSegmentDetailsPanel';
import StorefrontCartPanel from '../Storefront/Carts/StorefrontCartPanel';
import { PreparedCartPanel } from '../Storefront/PreparedCart/PreparedCartPanel';
import NewStorefrontDialog from '../Storefront/NewStorefrontPanel';
import StorefrontTemplatePickerModal, { IStorefrontTemplatePresets } from '../Storefront/Templates/StorefrontTemplatePickerModal';
import EditStorefrontTemplateModal from '../Storefront/Templates/EditStorefrontTemplateModal';
import { IStorefrontTemplate } from '../../models/Storefront';
import { useGetGlobalStorefrontSettingsQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import OpportunityPickerDialog from './OpportunityPickerDialog';
import { EntityRequest } from './sections';

interface ICreateEntityHostProps {
    request?: EntityRequest;
    onDismiss: () => void;
}

/**
 * Renders whichever create/details modal the global search palette requested — one at a time,
 * nothing mounted (and no queries fired) while idle. Every component here is the same standalone
 * dialog/panel its home page uses; an undefined entity prop means create mode.
 */
const CreateEntityHost: React.FC<ICreateEntityHostProps> = (props) => {
    const history = useHistory();
    const selectedCatalog = useSelector((state: RootState) => state.search.selectedCatalog);
    // Install-extension is a two-step flow: pick from the available list, then configure in the
    // details panel matching the pick's category (folio vs. regular) — same wiring as
    // ExtensionManagement's new-install path.
    const [selectedExtension, setSelectedExtension] = React.useState<IExtension | undefined>();
    // New storefronts inherit the org's global storefront defaults, same as the list page's flow.
    const globalStorefrontSettings = useGetGlobalStorefrontSettingsQuery(undefined, {
        skip: props.request?.kind !== 'createStorefront',
    });

    // Template-first storefront creation, same as the storefront list page's New flow.
    const [storefrontTemplate, setStorefrontTemplate] = React.useState<IStorefrontTemplate | undefined>();
    const [storefrontPresets, setStorefrontPresets] = React.useState<IStorefrontTemplatePresets | undefined>();
    const [storefrontTemplatePicked, setStorefrontTemplatePicked] = React.useState(false);
    const [editTemplateId, setEditTemplateId] = React.useState<string | undefined>();

    const request = props.request;
    if (!request) return null;

    const dismiss = () => {
        setSelectedExtension(undefined);
        setStorefrontTemplate(undefined);
        setStorefrontPresets(undefined);
        setStorefrontTemplatePicked(false);
        setEditTemplateId(undefined);
        props.onDismiss();
    };

    switch (request.kind) {
        case 'createWorkspace':
            return (
                <CreateWorkspaceDialog
                    isOpen
                    onDismiss={dismiss}
                    onCreated={(id) => history.push(`/workspace/${id}`)}
                />
            );
        case 'createContact':
            return <ContactDetailsModal isOpen onDismiss={dismiss} />;
        case 'contact':
            return <ContactDetailsModal isOpen contact={request.contact} onDismiss={dismiss} />;
        case 'createCompany':
            return <CompanyDetailsModal isOpen onClose={dismiss} />;
        case 'company':
            return <CompanyDetailsModal isOpen company={request.companyId} onClose={dismiss} />;
        case 'createStorefront':
            if (editTemplateId) {
                return <EditStorefrontTemplateModal isOpen templateId={editTemplateId} onDismiss={() => setEditTemplateId(undefined)} />;
            }
            if (!storefrontTemplatePicked) {
                return (
                    <StorefrontTemplatePickerModal
                        isOpen
                        onDismiss={dismiss}
                        onSelectTemplate={(template, presets) => {
                            setStorefrontTemplate(template);
                            setStorefrontPresets(presets);
                            setStorefrontTemplatePicked(true);
                        }}
                        onEditTemplate={(template) => setEditTemplateId(template.id)}
                    />
                );
            }
            return (
                <NewStorefrontDialog
                    isOpen
                    globalSettings={globalStorefrontSettings.data}
                    template={storefrontTemplate}
                    templatePresets={storefrontPresets}
                    onAddStorefront={dismiss}
                    onClose={dismiss}
                />
            );
        case 'createApiKey':
            return <NewApiKeyDialog isOpen onClose={dismiss} />;
        case 'createWebhook':
            return <WebhookPanel isOpen onDismiss={dismiss} />;
        case 'createTaxCode':
            return <EditTaxCodeModal isOpen onClose={dismiss} />;
        case 'taxCode':
            return <EditTaxCodeModal isOpen taxCode={request.taxCode} onClose={dismiss} />;
        case 'productCategory':
            return (
                <ProductCategoryDetailsPanel
                    isOpen
                    categoryIdentifier={request.categoryIdentifier}
                    segmentIdentifier={request.segmentIdentifier}
                    onDismiss={dismiss}
                />
            );
        case 'productSegment':
            return <ProductSegmentDetailsPanel isOpen segmentIdentifier={request.segmentIdentifier} onDismiss={dismiss} />;
        case 'installExtension':
            if (!selectedExtension) {
                return (
                    <AvailableExtensionsPanel
                        isOpen
                        onClose={dismiss}
                        onSelect={(extension) => setSelectedExtension(extension)}
                    />
                );
            }
            return selectedExtension.category === 'folio' ? (
                <ExtensionFolioDetailsPanel
                    isOpen
                    onClose={dismiss}
                    installedExtension={undefined}
                    extensionId={selectedExtension.id ?? ''}
                    isNew={false}
                />
            ) : (
                <ExtensionDetailsPanel
                    isOpen
                    onClose={dismiss}
                    installedExtension={undefined}
                    extensionId={selectedExtension.id ?? ''}
                    isNew
                />
            );
        case 'product':
            return (
                <ProductIdModal
                    catalog={selectedCatalog?.name ?? ''}
                    productId={request.productId}
                    addToDestination={request.destination}
                    initialTab={request.initialTab}
                    onDismiss={dismiss}
                />
            );
        case 'order':
            return <StorefrontOrderPanel order={request.orderId} onDismiss={dismiss} />;
        case 'cart':
            return <StorefrontCartPanel cartId={request.cartId} onDismiss={dismiss} />;
        case 'preparedCart':
            return <PreparedCartPanel visible workspaceId={request.workspaceId} onClose={dismiss} />;
        case 'preparedCartFromOpportunity':
            return (
                <OpportunityPickerDialog
                    extensionId={request.extensionId}
                    onDismiss={dismiss}
                    onPick={(opportunityId) => {
                        dismiss();
                        history.push(`/go/opportunity-import/${encodeURIComponent(request.extensionId)}?opp=${encodeURIComponent(opportunityId)}`);
                    }}
                />
            );
        default:
            return null;
    }
};

export default CreateEntityHost;
